import { type PartialRequired } from "../../../../types/internal/utility";

type WhereValue = string | number | {
    value: string;
};

export interface WhereClauseObject {
    field?: string;
    operator?: string;
    value: WhereValue;
    and: boolean;
}

export type WhereClause = string | WhereClauseObject;

export interface WhereClauseArray extends Array<WhereClause | WhereClauseArray> {}
export type WhereClauses = WhereClauseArray | WhereClause;

interface PrecisionClause {
    type: "range" | "aggregate";
    level?: string;
}

type StatusClauseType = "deleted" | "cancelled" | "voided" | "aborted";

interface StatusClause {
    visibility: "include" | "exclude" | "only";
    type?: StatusClauseType;
}

export interface ReportClauses {
    select: Array<string>;
    from: string;
    by: string;
    precision: false | PrecisionClause;
    on: Array<string>;
    where: Array<WhereClauses>;
    start: false | string;
    end: false | string;
    order: Array<{
        field: string;
        direction: "asc" | "desc";
    }>;
    consolidated: boolean;
    status: false | StatusClause;
    limit: false | number;
    offset: false | number;
}

type ReportParsedCharacter = "character" | "ignore" | "startQuote" | "endQuote" | "startBracket" | "endBracket";
type ReportParsedWord = "as" | "string" | "operator" | "total" | {
    clause: keyof ReportClauses;
    additional?: any;
};

interface ReportParseContext {
    previous: false | string;
    position: number;
    withinQuotes: false | {
        quoteType: "single" | "double";
    };
    words: Array<string>;
    wordFinished: boolean;
    currentClause: "" | keyof ReportClauses;
    bracketLevel: number;
}

export class ReportParseError extends Error {
    protected report: string;
    protected position: number | string;

    constructor(report: string, parsePosition: number | string, ...params: Array<string>) {
        super(...params);

        if(Error.captureStackTrace) {
            Error.captureStackTrace(this, ReportParseError);
        }

        this.report = report;
        this.position = parsePosition;
    }
}

export default class ReportParser {
    protected report: string;

    constructor(report: string) {
        this.report = report;
    }

    protected parseCharacter(character: string, context: ReportParseContext): ReportParsedCharacter {
        if(character.length > 1) {
            throw new ReportParseError(this.report, "Can not parse more than one character");
        }

        switch(character) {
            case "'":
                if(!context.withinQuotes) {
                    return "startQuote";
                }

                if(context.withinQuotes.quoteType === "double") {
                    return "character";
                }

                return "endQuote";
            case "\"":
                if(!context.withinQuotes) {
                    return "startQuote";
                }

                if(context.withinQuotes.quoteType === "single") {
                    return "character";
                }

                return "endQuote";
            case " ":
            case "\n":
            case "\r":
            case "\t":
                return "ignore";
            case "(":
                return "startBracket";
            case ")":
                return "endBracket";
            default:
                return "character";
        }
    }

    protected parseWord(word: string): ReportParsedWord {
        word = word.toLowerCase();

        if(word.slice(-1) === ",") {
            word = word.slice(0, -1);
        }

        switch(word) {
            case "select":
                return {
                    clause: "select",
                };
            case "from":
                return {
                    clause: "from",
                };
            case "range":
                return {
                    clause    : "precision",
                    additional: "range",
                };
            case "aggregate":
                return {
                    clause    : "precision",
                    additional: "aggregate",
                };
            case "by":
                return {
                    clause: "by",
                };
            case "on":
                return {
                    clause: "on",
                };
            case "where":
                return {
                    clause: "where",
                };
            case "start":
                return {
                    clause: "start",
                };
            case "end":
                return {
                    clause: "end",
                };
            case "order":
                return {
                    clause: "order",
                };
            case "consolidated":
                return {
                    clause    : "consolidated",
                    additional: true,
                };
            case "unconsolidated":
                return {
                    clause    : "consolidated",
                    additional: false,
                };
            case "limit":
                return {
                    clause: "limit",
                };
            case "offset":
                return {
                    clause: "offset",
                };
            case "as":
                return "as";
            case "include":
                return {
                    clause    : "status",
                    additional: "include",
                };
            case "exclude":
                return {
                    clause    : "status",
                    additional: "exclude",
                };
            case "only":
                return {
                    clause    : "status",
                    additional: "only",
                };
            case "+":
            case "-":
            case "/":
            case "%":
            case "*":
            case "^":
                return "operator";
            case "total(sum)":
            case "total(average)":
            case "total(mode)":
            case "total(median)":
            case "total(none)":
            case "total(percentage_from_total)":
                return "total";
            default:
                return "string";
        }
    }

    protected parseStringIntoWords(clause: string): Array<string> {
        const context: ReportParseContext = {
            previous     : false,
            position     : 0,
            withinQuotes : false,
            words        : [],
            wordFinished : true,
            currentClause: "",
            bracketLevel : 0,
        };

        for(let i = 0, l = clause.length; i < l; i++) {
            context.position = i;

            const result = this.parseCharacter(clause[i], context);

            context.previous = clause[i];

            if(context.wordFinished && result === "ignore") {
                continue;
            }

            if(result === "ignore" && !(context.withinQuotes || context.bracketLevel !== 0)) {
                context.wordFinished = true;
                continue;
            }

            if(context.wordFinished) {
                context.words.push("");
                context.wordFinished = false;
            }

            if(result === "startBracket") {
                context.bracketLevel++;
            }

            if(result === "endBracket") {
                context.bracketLevel--;

                if(context.bracketLevel < 0) {
                    throw new ReportParseError(
                        this.report,
                        i,
                        "Too many closed brackets compared to opened brackets"
                    );
                }
            }

            if(result === "startQuote") {
                if(context.withinQuotes) {
                    throw new ReportParseError(
                        this.report,
                        context.position,
                        "Invalid result determined (start quote)"
                    );
                }

                context.withinQuotes = {
                    quoteType: clause[i] === "\"" ? "double" : "single",
                };
            }

            if(result === "endQuote") {
                if(!context.withinQuotes) {
                    throw new ReportParseError(
                        this.report,
                        context.position,
                        "Invalid result determined (end quote)"
                    );
                }

                context.withinQuotes = false;
            }

            context.words[context.words.length - 1] += clause[i].toString();
        }

        if(context.withinQuotes) {
            throw new ReportParseError(this.report, context.position, "Never ending quotes");
        }

        if(context.bracketLevel !== 0) {
            throw new ReportParseError(
                this.report,
                context.position,
                "Never ending brackets"
            );
        }

        return context.words;
    }

    protected parseWhereWord(word: string): "operator" | "special" | "string" | "and" | "or" | "raw" {
        word = word.toLowerCase();

        switch(word) {
            case ">":
            case "<":
            case ">=":
            case "<=":
            case "<>":
            case "=":
            case "like":
            case "in":
                return "operator";
            case "is":
            case "not":
                return "special";
            case "null":
                return "raw";
            case "and":
                return "and";
            case "or":
                return "or";
            default:
                return "string";
        }
    }

    protected parseWhereClause(clause: Array<string>): Array<WhereClause> {
        // Reset the context
        const context = {
            clauseStarted: false,
            brackets     : false,
        };

        const parsed: Array<{
            field?: false | string;
            operator?: false | string;
            value: false | string | {
                value: string;
            };
            and: boolean;
        }> = [];

        for(let i = 0, l = clause.length; i < l; i++) {
            const result = this.parseWhereWord(clause[i]);
            let previous;

            if(parsed.length > 0) {
                previous = parsed[parsed.length - 1];
            }

            if(result === "operator") {
                if(!context.clauseStarted || !previous) {
                    throw new ReportParseError(
                        this.report,
                        clause[i],
                        "Cannot use operator before declaring field in where clause"
                    );
                }

                if(previous.operator !== false) {
                    if(
                        typeof previous.operator === "string" &&
                        previous.operator.toLowerCase() === "not"
                    ) {
                        parsed[parsed.length - 1].operator += " " + clause[i];
                    } else {
                        throw new ReportParseError(
                            this.report,
                            clause[i],
                            "Operator already specified"
                        );
                    }
                } else {
                    parsed[parsed.length - 1].operator = clause[i];
                }

                continue;
            }

            if(result === "special") {
                if(!context.clauseStarted || !previous) {
                    throw new ReportParseError(
                        this.report,
                        clause[i],
                        "Cannot use operator before declaring field in where clause"
                    );
                }

                if(previous.operator !== false) {
                    parsed[parsed.length - 1].operator = parsed[parsed.length - 1].operator + " " + clause[i];
                } else {
                    parsed[parsed.length - 1].operator = clause[i];
                }

                continue;
            }

            if(result === "and") {
                if(context.brackets && previous) {
                    context.brackets = false;

                    if(previous.operator === false) {
                        parsed[parsed.length - 1].value = {
                            value: previous.field as string,
                        };
                        delete parsed[parsed.length - 1].operator;
                        delete parsed[parsed.length - 1].field;
                    }
                }

                context.clauseStarted = true;
                parsed.push({
                    field   : false,
                    operator: false,
                    value   : false,
                    and     : true,
                });

                continue;
            }

            if(result === "or") {
                if(context.brackets && previous) {
                    context.brackets = false;

                    if(previous.operator === false) {
                        parsed[parsed.length - 1].value = {
                            value: previous.field as string,
                        };
                        delete parsed[parsed.length - 1].operator;
                        delete parsed[parsed.length - 1].field;
                    }
                }

                context.clauseStarted = true;
                parsed.push({
                    field   : false,
                    operator: false,
                    value   : false,
                    and     : false,
                });

                continue;
            }

            // String Parsing
            let tempClause = clause[i];
            let brackets = false;

            if(tempClause.charAt(0) === "(") {
                // parsed.push(this.parseWhereClause(this.parseStringIntoWords(clause[i].slice(1, -1))));
                // parsed.push(clause[i].slice(1, -1).trim());

                tempClause = tempClause.slice(1, -1).trim();
                brackets = true;
            }

            if(!context.clauseStarted || context.brackets) {
                if(context.brackets && previous) {
                    context.brackets = false;

                    if(previous.operator === false) {
                        parsed[parsed.length - 1].value = {
                            value: previous.field as string,
                        };
                        delete parsed[parsed.length - 1].operator;
                        delete parsed[parsed.length - 1].field;
                    }
                }

                context.clauseStarted = true;
                parsed.push({
                    field   : false,
                    operator: false,
                    value   : false,
                    and     : true,
                });

                previous = parsed[parsed.length - 1];
            }

            if(previous && previous.operator === false) {
                if(previous.field !== false) {
                    throw new ReportParseError(
                        this.report,
                        tempClause,
                        "Field already defined in where clause"
                    );
                }

                parsed[parsed.length - 1].field = tempClause;

                if(brackets) {
                    context.brackets = true;
                }

                continue;
            }

            if(previous && previous.value !== false) {
                throw new ReportParseError(
                    this.report,
                    tempClause,
                    "Value already defined in where clause"
                );
            }

            if(result === "raw") {
                parsed[parsed.length - 1].value = {
                    value: tempClause,
                };

                if(brackets) {
                    context.brackets = true;
                }

                continue;
            }

            parsed[parsed.length - 1].value = tempClause;

            if(brackets) {
                context.brackets = true;
            }
        }

        return parsed as Array<WhereClause>;
    }

    protected parseDate(date: string): string {
        if(date.charAt(0) === "\"" || date.charAt(0) === "'") {
            date = date.slice(1, -1);
        }

        return date;

        /*
        // TODO: Look at parsing the string later, need to write our own strtotime port from PHP to make it work.

        if(date.charAt(0) === '"') {
            date = date.slice(1, -1);
        }

        let parsed = moment(date);

        if(parsed.isValid()) {
            return parsed;
        }

        // Attempt to parse the date using locutus (php strtotime parser port)
        parsed = moment(strtotime(date));

        if(parsed.isValid()) {
            return parsed;
        }

        throw new ReportParseError(
            this.report,
            date,
            "We could not parse the date"
        );
        */
    }

    public parse(): ReportClauses {
        const clauses: ReportClauses & {
            where: Array<string> | WhereClauses;
        } = {
            select      : [],
            from        : "",
            by          : "",
            precision   : false,
            on          : [],
            where       : [],
            start       : false,
            end         : false,
            order       : [],
            consolidated: true,
            status      : false,
            limit       : false,
            offset      : false,
        };

        const parsed = {
            select      : false,
            from        : false,
            by          : false,
            precision   : false,
            on          : false,
            where       : false,
            start       : false,
            end         : false,
            order       : false,
            consolidated: false,
            status      : false,
            limit       : false,
            offset      : false,
        };

        const words = this.parseStringIntoWords(this.report);
        const context: PartialRequired<ReportParseContext, "currentClause"> = {
            currentClause: "",
        };

        for(let i = 0, l = words.length; i < l; i++) {
            const result = this.parseWord(words[i]);
            let castWord: string | number = words[i];

            if(result === "as") {
                if(context.currentClause !== "select") {
                    throw new ReportParseError(
                        this.report,
                        context.currentClause,
                        "AS is invalid outside of select clause"
                    );
                }

                const prev = clauses.select.pop();

                clauses.select.push(prev + " " + words[i] + " " + words[i + 1]);

                i++;
                continue;
            }

            if(result === "total") {
                if(context.currentClause !== "select") {
                    throw new ReportParseError(
                        this.report,
                        context.currentClause,
                        "TOTAL expression is invalid outside of select clause"
                    );
                }

                const prev = clauses.select.pop();

                clauses.select.push(`${prev} ${words[i]}`);

                continue;
            }

            if(result === "string") {
                if(context.currentClause === "precision") {
                    clauses.precision = clauses.precision as PrecisionClause;

                    if(typeof clauses.precision.level !== "undefined") {
                        throw new ReportParseError(
                            this.report,
                            context.currentClause,
                            "Precision level is already defined"
                        );
                    }

                    clauses.precision.level = words[i];

                    continue;
                }

                if(context.currentClause === "where") {
                    clauses.where.push(words[i]);

                    continue;
                }

                if(context.currentClause === "order") {
                    if(words[i].slice(-1) === ",") {
                        words[i] = words[i].slice(0, -1);
                    }

                    let direction: "asc" | "desc" = "asc";
                    let skip = false;
                    const DIRECTIONS = [ "asc", "ascending", "desc", "descending" ];

                    if(i + 1 < l) {
                        let nextWord = words[i + 1];

                        if(nextWord.slice(-1) === ",") {
                            nextWord = nextWord.slice(0, -1);
                        }

                        if(DIRECTIONS.includes(nextWord.toLowerCase())) {
                            const directionIndex = DIRECTIONS.indexOf(nextWord.toLowerCase());

                            if(directionIndex >= 2) {
                                direction = "desc";
                            }

                            skip = true;
                        }
                    }

                    if([ "\"", "'" ].includes(words[i].slice(0, 1)) && [ "\"", "'" ].includes(words[i].slice(-1))) {
                        words[i] = words[i].slice(1, -1);
                    }

                    clauses.order.push({
                        field: words[i],
                        direction,
                    });

                    if(skip) {
                        // Skip the next word
                        i++;
                    }

                    continue;
                }

                if(context.currentClause === "consolidated") {
                    throw new ReportParseError(
                        this.report,
                        context.currentClause,
                        "Consolidated clause cannot have a value"
                    );
                }

                if(context.currentClause === "status") {
                    clauses.status = clauses.status as StatusClause;

                    if(typeof clauses.status.type !== "undefined") {
                        throw new ReportParseError(
                            this.report,
                            context.currentClause,
                            "Status type already defined"
                        );
                    }

                    clauses.status.type = words[i] as StatusClauseType;

                    continue;
                }

                if([ "limit", "offset" ].includes(context.currentClause)) {
                    // Cast the word
                    castWord = parseInt(words[i]);

                    if(isNaN(castWord)) {
                        throw new ReportParseError(
                            this.report,
                            context.currentClause,
                            "Invalid value specified, expected number, received " + words[i]
                        );
                    }
                }

                if(context.currentClause === "from") {
                    if(clauses.from !== "") {
                        if(clauses.from.endsWith(",")) {
                            clauses.from += ` ${castWord}`;
                            continue;
                        }
                    }
                }

                if(Array.isArray(clauses[context.currentClause as keyof ReportClauses])) {
                    (clauses[context.currentClause as keyof ReportClauses] as Array<string | number>).push(castWord);
                } else {
                    (clauses[context.currentClause as keyof ReportClauses] as string | number) = castWord;
                }

                continue;
            }

            if(result === "operator") {
                // Remove the previous word from the clause
                let prev;

                if(Array.isArray(clauses[context.currentClause as keyof ReportClauses])) {
                    if((clauses[context.currentClause as keyof ReportClauses] as Array<string>).length === 0) {
                        throw new ReportParseError(
                            this.report,
                            context.currentClause,
                            "No previous word to apply operator (" + words[i] + ") on"
                        );
                    }

                    prev = (clauses[context.currentClause as keyof ReportClauses] as Array<string>).pop();
                } else {
                    if(!clauses[context.currentClause as keyof ReportClauses]) {
                        throw new ReportParseError(
                            this.report,
                            context.currentClause,
                            "No previous word to apply operator (" + words[i] + ") on"
                        );
                    }

                    prev = clauses[context.currentClause as keyof ReportClauses];
                }

                // Ensure we have another word
                if(i + 1 >= l) {
                    throw new ReportParseError(
                        this.report,
                        context.currentClause,
                        "No next word to apply operator (" + words[i] + ") to"
                    );
                }

                // Add the next word
                const operated = prev + " " + words[i] + " " + words[i + 1];

                if(Array.isArray(clauses[context.currentClause as keyof ReportClauses])) {
                    (clauses[context.currentClause as keyof ReportClauses] as Array<string>).push(operated);
                } else {
                    (clauses[context.currentClause as keyof ReportClauses] as string) = operated;
                }

                i++;
                continue;
            }

            // Check if the result has already been parsed
            if(parsed[result.clause as keyof ReportClauses]) {
                throw new ReportParseError(
                    this.report,
                    context.currentClause,
                    result.clause + " clause has already been parsed"
                );
            }

            // Finish parsing the previous clause
            if(context.currentClause) {
                parsed[context.currentClause] = true;
            }

            // Set the clause
            context.currentClause = result.clause;

            // Adjust any additional information
            switch(context.currentClause) {
                case "consolidated":
                    clauses.consolidated = result.additional;
                    break;
                case "precision":
                    clauses.precision = {
                        type: result.additional,
                    };
                    break;
                case "status":
                    clauses.status = {
                        visibility: result.additional,
                    };
                    break;
            }
        }

        // Validate and transform clauses
        if(clauses.select.length === 0) {
            throw new ReportParseError(
                this.report,
                "select",
                "Report is not selecting anything"
            );
        }

        for(let i = 0, l = clauses.select.length; i < l; i++) {
            if(clauses.select[i][clauses.select[i].length - 1] === ",") {
                clauses.select[i] = clauses.select[i].slice(0, -1);
            }
        }

        for(let i = 0, l = clauses.on.length; i < l; i++) {
            if(clauses.on[i][clauses.on[i].length - 1] === ",") {
                clauses.on[i] = clauses.on[i].slice(0, -1);
            }
        }

        if(clauses.where.length) {
            clauses.where = this.parseWhereClause(clauses.where as Array<string>);
        }

        if(clauses.start) {
            clauses.start = this.parseDate(clauses.start);
        }

        if(clauses.end) {
            clauses.end = this.parseDate(clauses.end);
        }

        return clauses;
    }
}
